/**
 * Prompt registry — one place to see, override, and localise every LLM
 * system prompt the SDK ships.
 *
 * Motivation. Before this file, prompts lived inline in ~10 subsystem
 * modules (webagent narrator, planner, InlineAgent, markdown-edit,
 * immersive-translate, voice-cleanup, Dwell classifier, task runner).
 * A host that wanted "Japanese prompts" or "no persona line" had to
 * spelunk each module. The registry consolidates all defaults behind
 * stable IDs and gives hosts three levers:
 *
 *   - `override(id, locale, render)` — full replace for one locale.
 *   - `append(id, render)`           — extra content stitched on the end.
 *   - `list()` / `has(id)`           — introspection.
 *
 * Locale fallback. `render(id, ctx, locale)` prefers a locale-specific
 * override, falls back to the `'en'` override, then to the SDK-registered
 * default. So a host can localise EN + zh-TW + ja without touching the
 * defaults for locales they don't care about.
 *
 * Non-goals. The registry does NOT parse or execute the prompt; it just
 * assembles the string. Runtime message plumbing (system role, temp,
 * tool schemas) stays in the subsystem callsites.
 */

/**
 * A prompt renderer. Receives an arbitrary context object (subsystem-
 * specific — e.g. the WebAgent narrator gets `PromptContext` with brand
 * + persona + sitemap; the InlineAgent gets nothing) and the effective
 * locale. Returns the full system prompt string.
 */
export type PromptRender<Ctx = unknown> = (ctx: Ctx, locale: string) => string;

/**
 * Stable IDs for every prompt the SDK ships. Consumers reference these
 * by string via the registry so hosts can override without importing
 * subsystem internals. Add here when introducing a new prompt.
 */
export const PROMPT_IDS = {
  /** InlineAgent — replaces a text fragment inside a longer body. */
  INLINE_EDIT: 'inline-edit.system',
  /** Markdown-doc edit tool (used by Plan runner). */
  MARKDOWN_EDIT: 'markdown-edit.system',
  /** Voice STT cleanup — turns raw transcript into polished prose. */
  VOICE_CLEANUP: 'voice-cleanup.system',
  /** Immersive-translate — inline machine translation of visible copy. */
  TRANSLATE: 'translate.system',
  /** Dwell classifier — labels the long-pressed element + suggests actions. */
  DWELL_CLASSIFY: 'dwell-classify.system',
  /** Planner — writes the CoT / task plan for the WebAgent to execute. */
  PLANNER: 'planner.system',
  /** WebAgent narrator — the huge everything-prompt for the agentic loop. */
  WEBAGENT_NARRATOR: 'webagent-narrator.system',
  /** WebAgent CoT variant — thinking-mode narrator. */
  WEBAGENT_COT: 'webagent-cot.system',
  /** Task runner — free-form task execution prompt (host-supplied by default). */
  TASK: 'task.system',
} as const;

export type PromptId = (typeof PROMPT_IDS)[keyof typeof PROMPT_IDS];

/** Fallback locale when a specific locale has no override. */
const FALLBACK_LOCALE = 'en';

export class PromptRegistry {
  private defaults = new Map<string, PromptRender>();
  private overrides = new Map<string, Map<string, PromptRender>>();
  private appenders = new Map<string, PromptRender[]>();

  /**
   * Register the SDK-shipped default for a prompt id. Called at module
   * init by the subsystem that owns the prompt (see `./defaults.ts`).
   * Hosts typically don't call this — they use `override()` instead.
   */
  registerDefault<Ctx = unknown>(id: string, render: PromptRender<Ctx>): void {
    this.defaults.set(id, render as PromptRender);
  }

  /**
   * Replace the SDK default for one locale. Later calls with the same
   * `(id, locale)` win — hosts can rebind at runtime (e.g. after the
   * user changes site language).
   *
   * @example
   *   dddk.prompts.override('inline-edit.system', 'ja', () => `あなたは…`);
   */
  override<Ctx = unknown>(id: string, locale: string, render: PromptRender<Ctx>): void {
    let localeMap = this.overrides.get(id);
    if (!localeMap) {
      localeMap = new Map();
      this.overrides.set(id, localeMap);
    }
    localeMap.set(locale, render as PromptRender);
  }

  /**
   * Append extra content on the end of the resolved prompt. Runs for
   * every locale — good for site-wide reminders ("always cite sources",
   * "never mention pricing") that shouldn't require re-writing the
   * whole prompt. Multiple `append()` calls stack in registration order.
   */
  append<Ctx = unknown>(id: string, extra: PromptRender<Ctx>): void {
    let list = this.appenders.get(id);
    if (!list) {
      list = [];
      this.appenders.set(id, list);
    }
    list.push(extra as PromptRender);
  }

  /**
   * Resolve a prompt id to its final string. Order of preference:
   *   1. `override(id, locale)` — exact locale match
   *   2. `override(id, 'en')`   — English fallback
   *   3. `registerDefault(id)`  — SDK default
   * All matching `append()` extras are joined on with double-newlines.
   */
  render<Ctx = unknown>(id: string, ctx?: Ctx, locale: string = FALLBACK_LOCALE): string {
    const localeMap = this.overrides.get(id);
    const render =
      localeMap?.get(locale)
      ?? localeMap?.get(FALLBACK_LOCALE)
      ?? this.defaults.get(id);
    if (!render) {
      throw new Error(
        `[dddk] No prompt registered for id '${id}'. `
        + `Registered ids: ${this.list().join(', ') || '(none)'}. `
        + `Register via dddk.prompts.registerDefault(id, render) or dddk.prompts.override(id, locale, render).`,
      );
    }
    const base = render(ctx as unknown, locale);
    const extras = this.appenders.get(id) ?? [];
    if (extras.length === 0) return base;
    return extras.reduce(
      (acc, fn) => `${acc}\n\n${fn(ctx as unknown, locale)}`,
      base,
    );
  }

  /** Is there any renderer (default or override) for this id? */
  has(id: string): boolean {
    return this.defaults.has(id) || this.overrides.has(id);
  }

  /** All registered ids, sorted for stable output. */
  list(): string[] {
    const ids = new Set<string>();
    for (const id of this.defaults.keys()) ids.add(id);
    for (const id of this.overrides.keys()) ids.add(id);
    return Array.from(ids).sort();
  }

  /**
   * Drop all overrides + appenders for one id. Does NOT touch the
   * default — that stays as the SDK shipped it. Useful for test setup /
   * "reset to defaults" UI in a host tuning tool.
   */
  reset(id: string): void {
    this.overrides.delete(id);
    this.appenders.delete(id);
  }
}

/**
 * Process-wide singleton. Subsystems reach for this at module-init to
 * register their defaults so consumers can override without needing a
 * DotDotDuck instance in hand. The DotDotDuck constructor also exposes
 * this same singleton as `.prompts` for the more idiomatic path.
 */
export const promptRegistry = new PromptRegistry();
